/**
 * ContactsView - Display and manage senior contacts
 * Shows emergency contacts at top, integrates with call flow
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Phone, AlertCircle, Loader, PhoneCall } from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactsView({ senior, onCallFamily, onCallContact }) {
  const [contacts, setContacts] = useState([]);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [regularContacts, setRegularContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const handleContactCall = (contact) => {
    console.log('[Device][Contacts] Call requested for contact:', contact.name);
    
    if (onCallContact && typeof onCallContact === 'function') {
      onCallContact(contact);
    } else {
      toast.info(`Anruf an ${contact.name} wird vorbereitet...`);
      // For now, just log - will be implemented with WebSocket trigger
      console.log('[Device][Contacts] Contact call trigger:', {
        contact_id: contact.id,
        contact_name: contact.name,
        contact_phone: contact.phone
      });
    }
  };

  useEffect(() => {
    if (senior?.id) {
      fetchContacts();
    }
  }, [senior]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/api/contacts/seniors/${senior.id}`);
      const allContacts = response.data || [];
      
      setContacts(allContacts);
      setEmergencyContacts(allContacts.filter(c => c.is_emergency));
      setRegularContacts(allContacts.filter(c => !c.is_emergency));
      
      console.log('[Contacts] Loaded', allContacts.length, 'contacts');
    } catch (err) {
      console.error('[Contacts] Error:', err);
      setError('Kontakte konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader className="w-16 h-16 mx-auto mb-4 animate-spin text-teal-600" />
          <p className="text-2xl text-gray-600">Kontakte werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Users className="w-24 h-24 mx-auto mb-4 text-gray-400" />
          <p className="text-2xl text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchContacts}
            className="px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white rounded-xl text-xl font-bold"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-4xl font-bold text-gray-900 mb-6">Kontakte</h1>

      {/* Main Family Call Button */}
      <button
        onClick={onCallFamily}
        className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-3xl shadow-2xl p-8 mb-8 text-left group transition-all"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-3xl font-bold mb-2">Familie anrufen</div>
            <div className="text-xl opacity-90">Videoanruf starten</div>
          </div>
          <PhoneCall className="w-16 h-16 group-hover:scale-110 transition-transform" />
        </div>
      </button>

      {/* Emergency Contacts */}
      {emergencyContacts.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-3xl p-6 mb-6">
          <h2 className="text-3xl font-bold text-red-900 mb-4 flex items-center gap-3">
            <AlertCircle className="w-8 h-8" />
            Notfallkontakte
          </h2>
          
          <div className="space-y-3">
            {emergencyContacts.map((contact) => (
              <div
                key={contact.id}
                className="bg-white rounded-2xl p-6 flex items-center justify-between group hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <Users className="w-8 h-8 text-red-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {contact.name}
                    </div>
                    {contact.relation && (
                      <div className="text-lg text-gray-600">{contact.relation}</div>
                    )}
                    <div className="text-lg text-gray-700 font-mono mt-1">
                      {contact.phone}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => handleContactCall(contact)}
                  className="w-14 h-14 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform"
                  title={`Anrufen: ${contact.name}`}
                >
                  <Phone className="w-7 h-7" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regular Contacts */}
      {regularContacts.length > 0 && (
        <div className="bg-white rounded-3xl shadow-2xl p-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
            <Users className="w-8 h-8 text-teal-600" />
            Weitere Kontakte
          </h2>
          
          <div className="space-y-3">
            {regularContacts.map((contact) => (
              <div
                key={contact.id}
                className="bg-gray-50 rounded-2xl p-6 flex items-center justify-between group hover:bg-gray-100 transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                    <Users className="w-8 h-8 text-teal-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {contact.name}
                    </div>
                    {contact.relation && (
                      <div className="text-lg text-gray-600">{contact.relation}</div>
                    )}
                    <div className="text-lg text-gray-700 font-mono mt-1">
                      {contact.phone}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => handleContactCall(contact)}
                  className="w-14 h-14 bg-teal-500 hover:bg-teal-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                  title={`Anrufen: ${contact.name}`}
                >
                  <Phone className="w-7 h-7" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {contacts.length === 0 && (
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">
          <Users className="w-24 h-24 mx-auto mb-4 text-gray-400" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Noch keine Kontakte
          </h2>
          <p className="text-xl text-gray-600">
            Ihre Familie kann Kontakte in der Family-App hinzufügen.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-3xl p-6 mt-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">
          ℹ️ Geplante Funktionen
        </h3>
        <ul className="space-y-2 text-xl text-gray-700">
          <li>• Direktanruf per Telefon</li>
          <li>• Video-Call zu einzelnen Kontakten</li>
          <li>• Kontaktfotos</li>
          <li>• Schnellwahl-Buttons</li>
        </ul>
      </div>
    </div>
  );
}
